import React from 'react';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ProxyFile from 'folder-items/ProxyFile';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {Subtemplate, TemplateCollectionSummary} from 'notebook/entry-templates/TemplateConstants';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {NotePart} from 'notebook/util/NoteUtilConstants';

class SubtemplateFile extends ProxyFile<Subtemplate, typeof SubtemplateStore> {
  static override initClass(): void {
    this.prototype.proxiedField = 'subtemplate';
  }

  templateCollection(): TemplateCollectionSummary {
    return this.proxied().templateCollection;
  }

  latestVersionId(): number {
    return this.proxied().latestVersionId;
  }

  getStore(): typeof SubtemplateStore {
    return SubtemplateStore;
  }

  getState(): ItemEditorState<Subtemplate, NotePart, 'subtemplate', 'subtemplateId'> {
    return this.getStore().getEditorState(this.id);
  }

  openSubtemplateVersion = (): void => {
    SubtemplateActions.loadVersion(this.id, this.latestVersionId());
  };

  override setBanner(): void {
    if (!this.isLatest()) {
      this.setBannerText(
        <DocumentBanner.PreviousVersion
          docType={ModelTypeConstants.SUBTEMPLATE}
          isEvaluationPersisted={Boolean(
            this.proxied()?.subtemplateVersion?.noteVersion?.noteRuntimeSnapshot?.isEvaluationPersisted
          )}
          onNavigateToLatest={() => {
            this.openSubtemplateVersion();
          }}
        />
      );
    } else if (this.isArchived()) {
      this.setBannerText(<DocumentBanner.Archived docType={ModelTypeConstants.SUBTEMPLATE} />);
    } else if (!this.writable()) {
      this.setBannerText(<DocumentBanner.ReadOnly docType={ModelTypeConstants.SUBTEMPLATE} />);
    } else {
      this.setBannerText(null);
    }
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord): void {
    // Do not bump version and trigger an autosave because archiveRecord is not a versioned field  and has
    // already been posted to the server. See `updateItem` in VersioneditemSubstore
    SubtemplateActions.update(this.id, {$merge: {archiveRecord}}, {cidIdMap: null, bumpVersion: false});
  }

  override setPermissions(permissions: Permissions): void {
    // Do not bump version and trigger an autosave because archiveRecord is not a versioned field  and has
    // already been posted to the server. See `updateItem` in VersioneditemSubstore
    SubtemplateActions.update(this.id, {$merge: {permissions}}, {cidIdMap: null, bumpVersion: false});
  }

  load({versionId, deferred}: {versionId?: number | null; deferred: JQueryDeferred<void>}): void {
    if (this.proxied() != null && versionId != null) {
      // If it's already loaded and they are trying to load a version
      SubtemplateActions.loadVersion(this.id, versionId, {deferred});
    } else {
      // Otherwise, trigger a fresh open.
      SubtemplateActions.open(this.id, {versionId, deferred});
    }
  }

  override cleanUp(): void {
    super.cleanUp();
    SubtemplateActions.close(this.id);
  }

  override async triggerUpdateNameAction(name: string): Promise<string> {
    if (name !== this.name()) {
      await SubtemplateActions.saveFileData(this.id, {data: {name}});
    }
    return this.name();
  }
}
SubtemplateFile.initClass();
export default SubtemplateFile;
